import { SeverityLevel } from '@microsoft/applicationinsights-web';

import type {
    ApiCreateUserAddressRequest,
    ApiUpdateUserAddressRequest,
} from '../../api/user/hybrisUser/hybrisUserDirectApiRequests';
import type { HybrisUserInfo } from '../../models/hybris/hybrisUserInfo';
import type { InvoiceAddress, MunicipalUserInfo } from './userModels';

import { appConfig } from '../../../../appConfig';
import { appInsights } from '../../../../appInsights';
import { getAuthToken, getAuthTokenOrDefault } from '../../api/auth/getAuthToken';
import { municipalityDirectApi } from '../../api/municipality/municipalityDirectApi';
import { SyncronQueue } from '../../api/SyncronQueue';
import { hybrisUserDirectApi } from '../../api/user/hybrisUser/hybrisUserDirectApi';
import { userProfileDirectApi } from '../../api/userProfile/userProfileDirectApi';
import persistance from '../../persistance/persistance';
import { type UserAddress } from '../../store/structureDefinitions/userState';
import { isB2BWindowLocation, isInB2BCrossContext } from '../../utils/b2bUtils';
import { type CustomerGroup, getAnonymousCustomerGroups } from '../../utils/customerGroupsUtils';
import { UserType } from './userModels';

const userFlowQueue = new SyncronQueue();
const userInfoQueue = new SyncronQueue();
const customersGroupQueue = new SyncronQueue();

const createAddress = async (request: ApiCreateUserAddressRequest) => {
    const { token, shoppingUserId } = await getAuthToken();
    const userAddress = await hybrisUserDirectApi.createAddress(token)(shoppingUserId, request);
    return userAddress;
};

const updateAddress = async (address: ApiUpdateUserAddressRequest) => {
    const { token, shoppingUserId } = await getAuthToken();
    await hybrisUserDirectApi.updateAddress(token)(shoppingUserId, address.id, address);
    return address;
};

const deleteAddress = async (addressId: string) => {
    const { token, shoppingUserId } = await getAuthToken();
    await hybrisUserDirectApi.deleteAddress(token)(shoppingUserId, addressId);
    return addressId;
};

const getUserType = () => {
    return userFlowQueue.run(
        async () => {
            const identity = await getAuthTokenOrDefault();

            if (identity.shoppingUserId === 'anonymous') {
                return UserType.anonymous;
            }

            // order is important since hybris returns company for punchout user aswell
            if (identity.isPunchout) {
                return UserType.punchout;
            }

            const customerGroups = await getUserCustomerGroups(
                identity.shoppingUserId,
                !!identity.token,
            );

            if (customerGroups.includes('CUSTOMER_COMPANY')) {
                return UserType.company;
            }

            return UserType.private;
        },
        { dontPushBack: true, uniqueName: 'getUserType' },
    );
};

const customerGroupsMap: Record<string, CustomerGroup> = {
    businessgroup: 'CUSTOMER_COMPANY',
    b2bgroup: 'CUSTOMER_COMPANY',
    privatecustomergroup: 'CUSTOMER_PRIVATE',
    // this is being used in search - if search does not know this value it will break,
    // talk with others/loop if you want to uncomment this
    // ikpgroup: 'CUSTOMER_IKP',
};

const loadCustomerGroups = async () => {
    const userId = appConfig.coopUserSettings.shoppingUserId;
    try {
        const { token } = await getAuthToken();
        const result = await hybrisUserDirectApi.getCustomerGroups(token)(userId);
        const hybrisCustomerGroups =
            result?.userGroups?.filter((x) => !!x.uid).map((x) => x.uid) ?? [];
        const customersGroup = hybrisCustomerGroups.reduce((groups, hGroup) => {
            if (customerGroupsMap[hGroup]) {
                return [...groups, customerGroupsMap[hGroup]];
            }
            return groups;
        }, [] as CustomerGroup[]);

        // this information is still unreliable in hybris, so we still take it from login
        if (
            !appConfig.coopUserSettings.isShoppingForAnother &&
            appConfig.coopUserSettings.isMedmeraCustomer
        ) {
            customersGroup.push('CUSTOMER_MEDMERA');
        }

        return customersGroup;
    } catch (_error) {
        const exError: Error = {
            name: 'Frontend - customer groups load fail',
            message: `Frontend - unable to load customer groups for user: ${userId}`,
            stack: undefined,
        };

        appInsights.trackException({
            exception: exError,
            severityLevel: SeverityLevel.Error,
        });

        // fallback to local customer groups
        return getAnonymousCustomerGroups();
    }
};

const getUserCustomerGroups = (userId: string | null, isAuthenticated: boolean) =>
    customersGroupQueue.run(
        async () => {
            if (!isAuthenticated || !userId) {
                return getAnonymousCustomerGroups();
            }

            let customerGroups = persistance.customerGroupsSession.get(userId);

            if (customerGroups === undefined) {
                customerGroups = await loadCustomerGroups();
                persistance.customerGroupsSession.add(customerGroups, userId);
            }

            return customerGroups;
        },
        { dontPushBack: true, uniqueName: 'getCustomerGroups' },
    );

const getHybrisUserInfo = (noCache = false) => {
    return userInfoQueue.run(
        async () => {
            const identity = await getAuthTokenOrDefault();
            const shoppingUserId = identity?.shoppingUserId;
            const token = identity?.token;

            if (!shoppingUserId || shoppingUserId === 'anonymous') {
                return undefined;
            }

            let hybrisUserInfo: HybrisUserInfo | undefined = !noCache
                ? persistance.hybrisUserInfoSession.getHybrisUserInfo(shoppingUserId)
                : undefined;

            if (!hybrisUserInfo && token) {
                hybrisUserInfo = await hybrisUserDirectApi.getHybrisUserInfo(token)(shoppingUserId);
            }

            if (hybrisUserInfo) {
                persistance.hybrisUserInfoSession.setHybrisUserInfo(shoppingUserId, hybrisUserInfo);
            }

            return hybrisUserInfo;
        },
        { dontPushBack: true, uniqueName: 'getHybrisUserInfo' },
    );
};

const getHybrisMunicipalUserInfo = (): Promise<MunicipalUserInfo | undefined> => {
    return userInfoQueue.run(
        async () => {
            const identity = await getAuthTokenOrDefault();

            if (
                identity.shoppingUserId === 'anonymous' ||
                !identity.shoppingUserId ||
                !identity.token
            ) {
                return undefined;
            }

            const hybrisUserInfo = await hybrisUserDirectApi.getHybrisUserInfo(identity.token)(
                identity.shoppingUserId,
            );

            const municipalInfo: MunicipalUserInfo = {
                firstName: hybrisUserInfo.firstName,
                lastName: hybrisUserInfo.lastName,
                municipalityCode: hybrisUserInfo.municipalityCode,
                municipalityUnitCode: hybrisUserInfo.municipalityUnitCode,
            };

            return municipalInfo;
        },
        {
            dontPushBack: true,
            uniqueName: 'getHybrisMunicipalUserInfo',
        },
    );
};

const getPunchoutUnit = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    const punchoutUnit = await hybrisUserDirectApi.getUserPunchoutUnit(token)(shoppingUserId);
    return punchoutUnit;
};

const getPunchoutAddresses = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    const punchoutUnit = await hybrisUserDirectApi.getUserPunchoutUnit(token)(shoppingUserId);
    const punchoutAddresses = await hybrisUserDirectApi.getAllPunchoutAddresses(token)(
        punchoutUnit.orgUnit.uid,
    );
    return punchoutAddresses.addresses;
};

const getMunicipalAddresses = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    let userAddresses: UserAddress[] = [];

    const user = await hybrisUserDirectApi.getHybrisUserInfo(token)(shoppingUserId);
    if (!user.defaultAddress && user.municipalityCode && user.municipalityUnitCode) {
        const municipalUnit = await municipalityDirectApi.getMunicipalityUnit(token)({
            municipalityId: user.municipalityCode,
            unitId: user.municipalityUnitCode,
        });
        userAddresses = municipalUnit?.contact ? [municipalUnit.contact] : [];
    }

    userAddresses = user.defaultAddress ? [user.defaultAddress] : [];
    return userAddresses;
};

const fetchAddresses = async (isPunchout: boolean) => {
    let userAddresses: UserAddress[] = [];
    if (isPunchout) {
        userAddresses = await getPunchoutAddresses();
    } else {
        userAddresses = await fetchUserAddresses();
    }

    return userAddresses;
};

const fetchUserAddresses = async (take?: number, removeDuplicateAddresses?: boolean) => {
    const { token, shoppingUserId } = await getAuthToken();
    let userAddresses: UserAddress[] = [];

    if (appConfig.coopUserSettings.isShoppingForMunicipal) {
        userAddresses = await getMunicipalAddresses();
    } else {
        userAddresses = (await hybrisUserDirectApi.getAllAddresses(token)(shoppingUserId)) ?? [];
    }

    userAddresses.reverse();

    if (removeDuplicateAddresses) {
        // Function to check if two addresses are the same
        const areAddressesEqual = (address1: UserAddress, address2: UserAddress) => {
            return (
                address1.firstName === address2.firstName &&
                address1.lastName === address2.lastName &&
                address1.town === address2.town &&
                address1.postalCode === address2.postalCode &&
                address1.line1 === address2.line1
            );
        };

        // Remove duplicate addresses
        userAddresses = userAddresses.filter((address, index, self) => {
            return self.findIndex((a) => areAddressesEqual(address, a)) === index;
        });
    }

    if (take) {
        userAddresses = userAddresses.slice(0, take);
    }

    return userAddresses;
};

const fetchProfile = async () => {
    const response = await userProfileDirectApi.getCurrentUserProfile();
    return response;
};

const updateFoodRegisteredCompany = async (foodRegisteredCompany: 'TRUE' | 'FALSE' | 'NOT_SET') => {
    const { token, shoppingUserId } = await getAuthToken();
    return hybrisUserDirectApi.updateHybrisUserInfo(token)(shoppingUserId, foodRegisteredCompany);
};

const getCompensationVochers = async () => {
    const { token, shoppingUserId } = await getAuthToken();
    return hybrisUserDirectApi.getCompensationVouchers(token, shoppingUserId);
};

/**
 * Get customer groups for the user.
 * If called inside B2B context, it will return fake customer groups so that cross-context mode feels like anonymous mode.
 */
const getB2BContextCustomerGroups = async (): Promise<CustomerGroup[]> => {
    if (appConfig.coopSettings.featureFlags.enableB2B) {
        const { privateUserOnCompanySite, companyUserOnPrivateSite } = isInB2BCrossContext({
            isB2BRoute: isB2BWindowLocation(),
            isUserCompany: appConfig.coopUserSettings.isCompany,
            isAuthenticated: appConfig.coopUserSettings.isAuthenticated,
        });
        if (privateUserOnCompanySite) {
            return ['CUSTOMER_COMPANY'];
        }
        if (companyUserOnPrivateSite) {
            return ['CUSTOMER_PRIVATE', 'CUSTOMER_MEDMERA'];
        }
    }

    return userFlow.getUserCustomerGroups(
        appConfig.coopUserSettings.shoppingUserId,
        appConfig.coopUserSettings.isAuthenticated,
    );
};

const getInvoiceAddress = async (): Promise<InvoiceAddress | undefined> => {
    const { token, shoppingUserId } = await getAuthToken();
    const hybrisUserInfoWithInvoice =
        await hybrisUserDirectApi.getInvoiceAddress(token)(shoppingUserId);

    if (hybrisUserInfoWithInvoice && hybrisUserInfoWithInvoice.billingAddress) {
        return {
            organizationNumber: hybrisUserInfoWithInvoice.companyNumber,
            ...hybrisUserInfoWithInvoice.billingAddress,
        };
    }

    return undefined;
};

const userFlow = {
    createAddress,
    updateAddress,
    deleteAddress,
    fetchAddresses,
    fetchUserAddresses,
    getUserType,
    getHybrisUserInfo,
    getUserCustomerGroups,
    getB2BContextCustomerGroups,
    fetchProfile,
    getHybrisMunicipalUserInfo,
    updateFoodRegisteredCompany,
    getCompensationVochers,
    getPunchoutUnit,
    getInvoiceAddress,
};

export default userFlow;
